import { Box } from '@chakra-ui/react';
import { useField } from 'formik';
import { useRef, useState } from 'react';
import { KInput } from '../../atoms/KInput';
import { currencyFormatterHelpers } from '@/utils/currencyFormatter';
import {
  handleNumberInputChange,
  handleNumberPaste,
  isNumberKeyAllowed,
} from '@/components/formBuilder/formFields/InputFields/numberFieldValidation';

const { formatCurrencyNumber, cleanCurrencyInput } = currencyFormatterHelpers;

const TOOLTIP_HIDE_AFTER_MS = 2000;

// Stable identity reference so consumers that don't pass debounceOnType
// don't trigger a per-keystroke setTimeout schedule.
const noopDebounce = (_value: string) => {};

interface InputFieldProps {
  item?: {
    name?: string;
    inputType?: string;
    placeholder?: string;
    label?: string;
    maxLength?: number;
    disabled?: boolean;
    CustomLeftElement?: React.ElementType;
    CustomRightElement?: React.ElementType;
    endAddon?: React.ReactNode;
    endAddonWrapperProps?: any;
    inputRef?: React.RefObject<HTMLInputElement>;
    showErrorStyles?: boolean;
    isRequired?: boolean;
    formatType?: string;
    currencyFieldName?: string; // New prop to link to currency field
    currencies?: Array<{ id: number; currency: string }>; // Currencies data
    minValue?: number;
    maxValue?: number;
    stepValue?: number;
    isInputSubmit?: boolean;
    customValidation?: (
      _value: string,
      _event: React.ChangeEvent<HTMLInputElement>,
      _onChange: (_e: React.ChangeEvent<HTMLInputElement>) => void
    ) => void;
    AfterInputGroupContent?: React.ElementType;
    inlineAfterInputGroupContent?: boolean;
    customOnChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
    customOnKeyDown?: (_event: React.KeyboardEvent<HTMLInputElement>) => void;
    beforeMaxLength?: boolean;
    afterMaxLength?: boolean;
    debounceOnType?: (_value: string) => void;
    autoFocus?: boolean;
    customLeftElementProps?: any;
    customRightElementProps?: any;
    customMaxLengthProps?: any;
    fieldType?: string;
    style?: Record<string, string>;
    inputStyle?: Record<string, any>;
    onPaste?: (
      _e: React.ClipboardEvent<HTMLInputElement>,
      _setValue: (_v: string) => void
    ) => void;
    inputHeight?: string | number;
  };
  field?: any;
  formik?: any;
}

export const KInputField = ({ item = {}, field, formik }: InputFieldProps) => {
  const {
    name,
    inputType,
    placeholder = '',
    label,
    maxLength,
    CustomRightElement,
    CustomLeftElement,
    endAddon,
    endAddonWrapperProps,
    customLeftElementProps,
    customRightElementProps,
    inputRef,
    // showErrorStyles = false,
    isRequired,
    formatType,
    currencyFieldName, // New prop to link to currency field
    minValue = 0,
    maxValue,
    stepValue,
    isInputSubmit = false,
    customValidation,
    AfterInputGroupContent,
    inlineAfterInputGroupContent = false,
    customOnChange = () => {},
    customOnKeyDown,
    // beforeMaxLength,
    customMaxLengthProps,
    // afterMaxLength = true,
    debounceOnType = noopDebounce,
    autoFocus = false,
    disabled = false,
    fieldType,
    style,
    inputStyle,
    onPaste,
    inputHeight,
  } = item;

  const [_field, meta, helpers] = useField(name);
  const { setTouched, setValue } = helpers;
  const { submitCount } = formik || {};
  const { touched } = meta || {};

  // Get currency field value if currencyFieldName is provided
  const [, currencyMeta] = useField(
    currencyFieldName || 'dummy_currency_field'
  );
  const selectedCurrencyId = currencyFieldName ? currencyMeta?.value : null;

  const intervalRef = useRef(null);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tooltipMessage, setTooltipMessage] = useState('');
  const showError = (submitCount > 0 || touched) && meta?.error && isRequired;
  const localInputRef = useRef<HTMLInputElement>(null);

  // Treat a field as numeric if either signal is set — config across the
  // codebase uses both inputType='number' (filter/form configs) and
  // fieldType='number' (offer form inline fields).
  const isNumberField = inputType === 'number' || fieldType === 'number';

  // Surfaces validator messages ("Enter numbers only", "Invalid number format")
  // in a Chakra tooltip and auto-hides after a short delay.
  const triggerTooltip = (message: string) => {
    setTooltipMessage(message);
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipMessage('');
    }, TOOLTIP_HIDE_AFTER_MS);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (isNumberField && handleNumberPaste(e, triggerTooltip)) {
      // Paste rejected by validator — don't fall through to onPaste.
      return;
    }
    if (onPaste) onPaste(e, setValue);
  };

  // Return null if name is not provided as it's required for useField
  if (!item || !name) return null;

  // Function to format a number as currency as thousand separators
  const formatNumberWithCommas = (value) => {
    if (!value) return '';

    const numericValue = (value?.toString() || '')
      .split('.')[0]
      .replace(/[^\d]/g, '');

    // Format the number with commas
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Get the selected currency string from the currencies data
  const getSelectedCurrencyString = () => {
    if (!currencyFieldName || !selectedCurrencyId || !item.currencies)
      return null;

    // Find the selected currency in the currencies array
    const selectedCurrency = item.currencies.find(
      (c) => c.id === selectedCurrencyId
    );
    return selectedCurrency?.currency || null;
  };

  // Format the value based on formatType and selected currency. Use `??` so
  // the number 0 isn't dropped to '' (would erase the display when a user
  // legitimately enters zero).
  const formattedValue = (() => {
    const rawValue = field?.value ?? '';
    if (formatType === 'currency') {
      const currencyString = getSelectedCurrencyString();
      if (currencyString && currencyFieldName) {
        return formatCurrencyNumber(rawValue, currencyString);
      }
      // Fallback to comma formatting if no currency is selected
      return formatNumberWithCommas(rawValue);
    }
    return rawValue;
  })();

  const handleOnChange = (event) => {
    const value = event.target.value;
    customOnChange(event);

    // Only schedule the debounced consumer when one was actually provided —
    // creating a 1s timer on every keystroke (per field) is pure overhead
    // when debounceOnType is the default no-op.
    if (debounceOnType !== noopDebounce) {
      clearTimeout(intervalRef.current);
      intervalRef.current = setTimeout(() => {
        debounceOnType(value);
      }, 1000);
    }

    if (customValidation) {
      customValidation(value, event, field.onChange);
    }
    // Number fields: delegate to the shared validation helper so KInputField
    // and the legacy InputField stay in lockstep on separator handling,
    // safe-integer preservation, leading-zero rules, etc.
    else if (isNumberField) {
      handleNumberInputChange(value, setValue, triggerTooltip);
    } else if (formatType !== 'currency') {
      field.onChange(event);
    } else {
      // For currency fields, clean the input and store only numeric value
      const cleanedValue = cleanCurrencyInput(value);
      setValue(cleanedValue);
    }
  };

  const inlineAfterContent =
    inlineAfterInputGroupContent && !!AfterInputGroupContent;

  return (
    <Box
      position="relative"
      w="100%"
      {...(inlineAfterContent
        ? { display: 'flex', alignItems: 'center', gap: '12px' }
        : {})}
    >
      {/* Floating validator message — absolutely positioned so it never
          competes with the input for layout space. Mirrors the look of the
          Chakra Tooltip used by InputField. */}
      {tooltipMessage && (
        <Box
          position="absolute"
          left="0"
          top="calc(100% + 4px)"
          bg="secondary-950"
          color="white"
          fontSize="14px"
          px="8px"
          py="4px"
          borderRadius="4px"
          whiteSpace="nowrap"
          zIndex={2}
          pointerEvents="none"
        >
          {tooltipMessage}
        </Box>
      )}
      <KInput
        {...field}
        onChange={handleOnChange}
        onClick={() => {
          if (inputRef?.current) {
            inputRef.current?.focus();
          } else if (localInputRef?.current) {
            localInputRef.current?.focus();
          }
        }}
        ref={inputRef || localInputRef}
        id={name}
        autoComplete="off"
        type={
          formatType === 'currency' || isNumberField
            ? 'text'
            : inputType || 'text'
        }
        inputMode={
          (formatType === 'currency' && fieldType === 'number') || isNumberField
            ? 'decimal'
            : undefined
        }
        value={formattedValue}
        placeholder={
          placeholder || `Enter ${label ? label?.toLowerCase() : ''}`
        }
        maxLength={maxLength}
        min={minValue}
        max={maxValue}
        step={stepValue}
        error={showError}
        onFocus={() => setTouched(false)}
        onBlur={() => setTouched(true)}
        disabled={disabled}
        onKeyDown={(e) => {
          if (customOnKeyDown) {
            customOnKeyDown(e);
          }
          if (isNumberField) {
            if (!isNumberKeyAllowed(e)) {
              e.preventDefault();
            } else if (e.key === '0') {
              // Match InputField behavior — block leading zero (e.g. "012")
              // so the change-handler's leading-zero rejection isn't reached
              // mid-typing.
              const input = e.currentTarget;
              if (input.selectionStart === 0 && input.value !== '') {
                e.preventDefault();
              }
            }
          }
          if (e.key === 'Enter') {
            e.preventDefault(); // Prevent the default form submission
            if (formik && isInputSubmit) {
              formik.submitForm(); // Manually submit the form
            }
          }
        }}
        style={style}
        inputStyle={inputStyle}
        autoFocus={autoFocus}
        onPaste={handlePaste}
        CustomRightElement={CustomRightElement}
        endAddon={endAddon}
        endAddonWrapperProps={endAddonWrapperProps}
        AfterInputGroupContent={AfterInputGroupContent}
        inlineAfterInputGroupContent={inlineAfterInputGroupContent}
        CustomLeftElement={CustomLeftElement}
        customLeftElementProps={customLeftElementProps}
        customRightElementProps={customRightElementProps}
        customMaxLengthProps={customMaxLengthProps}
        fieldType={
          formatType === 'currency' || isNumberField ? undefined : fieldType
        }
        inputHeight={inputHeight}
      />
    </Box>
  );
};
